import { useState } from "react";
import { toast } from "sonner";
import { Bot, ArrowRight, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RoboticsTeaser = () => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/robotics-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error("failed");
      data.status === "duplicate"
        ? toast.success("You're already on the robotics list.")
        : toast.success("You're on the list. We'll be in touch when it goes public.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Email us at info@truelifeentertainment.com");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="robotics-section" className="bg-charcoal-900 py-20 text-white sm:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <div className="flex items-center gap-3">
            <Eyebrow dark>Robotics Initiative</Eyebrow>
            <span className="border border-violet-400/40 bg-violet-400/10 px-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.25em] text-violet-400">
              Coming Soon
            </span>
          </div>
          <h2 className="mt-4 max-w-lg font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl lg:text-4xl">
            The new form of robotics for the entertainment industry.
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-400">
            We are developing and introducing the new form of robotics to live entertainment,
            designed and built by the stagehands and engineers who run the show every night.
          </p>
          <div className="mt-8 flex items-center gap-3 border border-white/10 bg-charcoal-800 p-5">
            <Bot className="h-5 w-5 shrink-0 text-violet-400" strokeWidth={1.8} />
            <p className="text-sm leading-relaxed text-slate-300">
              Built from the crew side. Revealed to the industry first.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            data-testid="robotics-signup-form"
            onSubmit={submit}
            className="border border-white/10 bg-charcoal-800 p-8 sm:p-9"
          >
            <h3 className="font-display text-xl font-bold">Be first in line.</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              Join the list and get one email when the robotics initiative goes public. No spam.
            </p>
            <label htmlFor="rb-email" className="mb-2 mt-7 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
              Email
            </label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                id="rb-email"
                type="email"
                data-testid="robotics-email-input"
                className="w-full border border-white/15 bg-charcoal-900 px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-all duration-200 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/25"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button
                type="submit"
                data-testid="robotics-signup-button"
                disabled={sending}
                className="group inline-flex shrink-0 items-center justify-center gap-2 bg-accent px-7 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-accent-dark disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                {sending ? "Joining..." : "Notify Me"}
              </button>
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              True Life Entertainment · Robotics Initiative
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default RoboticsTeaser;
